/**
 * Mutu Ancak PDF Generation Utility
 * Generates formatted PDF reports for quality inspection data
 * Using small font sizes to fit A4 paper
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MutuAncakData, MutuAncakGroupedData, PenaltyRate } from '../types';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { trackDownload } from '../../../activity';

// Format number: limit decimals to 2 if present (leave integers as-is)
const fmt = (val: any) => {
    if (val === '' || val === null || val === undefined) return '' as any;
    if (typeof val === 'number') return Number.isInteger(val) ? val : Number(val.toFixed(2));
    return val;
};

// Penalty code descriptions - sesuai dengan gambar
const PENALTY_DESCRIPTIONS = {
    P00: 'Tidak ada temuan losses panen',
    P01: 'TBS matang tidak dipanen (Janjang)',
    P02: 'Brondolan tidak dikutip (Kilogram)',
    P03: 'Pelepah sengkleh atau tidak dipotong mepet (Pokok)',
    P04: 'Pelepah tidak disusun rapi di gawangan mati (Pokok)',
    P05: 'Buah tidak diangkut ke TPH atau buah peram (Janjang)',
    P06: 'Buah busuk tidak diturunkan (Janjang)',
    P07: 'Buah bintang (Janjang)',
};

/**
 * Clean supervisor name - remove / NIP suffix if present
 */
const cleanSupervisorName = (supervisor: string): string => {
    if (!supervisor) return '';
    return supervisor.split('/')[0].trim();
};


const formatNumber = (num: number) => {
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(num);
};

const formatDecimal = (num?: number | null) => {
    if (!num) return '';
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2
    }).format(num);
};

/**
 * Group data by supervisor
 */
export const groupDataBySupervisor = (data: MutuAncakData[]): MutuAncakGroupedData[] => {
    const grouped = new Map<string, MutuAncakGroupedData>();

    data.forEach(item => {
        if (!grouped.has(item.supervisor)) {
            grouped.set(item.supervisor, {
                supervisor: item.supervisor,
                job_name: item.job_name,
                estate: item.estate,
                divisi: item.divisi,
                asisten: item.asisten,
                manager: item.manager,
                askep: item.askep,
                job_level: item.job_level,
                items: [],
            });
        }
        grouped.get(item.supervisor)!.items.push(item);
    });

    return Array.from(grouped.values());
};

/**
 * Calculate subtotals for a group of items by pemanen
 */
const calculateSubtotals = (items: MutuAncakData[]) => {
    const subtotals = {
        pkk_diperiksa: 0,
        pkk_panen: 0,
        jjg_dipanen: 0,
        P00: 0,
        P01: 0,
        P02: 0,
        P03: 0,
        P04: 0,
        P05: 0,
        P06: 0,
        P07: 0,
        total_rupiah: 0,
    };

    items.forEach(item => {
        subtotals.pkk_diperiksa += item.pkk_diperiksa || 0;
        subtotals.pkk_panen += item.pkk_panen || 0;
        subtotals.jjg_dipanen += item.jjg_dipanen || 0;
        subtotals.P00 += item.P00;
        subtotals.P01 += item.P01;
        subtotals.P02 += item.P02;
        subtotals.P03 += item.P03;
        subtotals.P04 += item.P04;
        subtotals.P05 += item.P05;
        subtotals.P06 += item.P06;
        subtotals.P07 += item.P07;
        subtotals.total_rupiah += item.total_rupiah || 0;
    });

    return subtotals;
};

/**
 * Group items by pemanen for subtotals
 */
const groupByPemanen = (items: MutuAncakData[]): Map<string, MutuAncakData[]> => {
    const grouped = new Map<string, MutuAncakData[]>();

    items.forEach(item => {
        if (!grouped.has(item.pemanen)) {
            grouped.set(item.pemanen, []);
        }
        grouped.get(item.pemanen)!.push(item);
    });

    return grouped;
};



/**
 * Generate PDF for a single mandor group
 */
export const generateMutuAncakPDF = (
    groupedData: MutuAncakGroupedData[],
    inspectionDate: string,
    penaltyRates?: PenaltyRate[]
) => {
    // A4 Portrait orientation
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margins = { top: 8, bottom: 20, left: 5, right: 5 };

    // Process each mandor group (each on a new page)
    groupedData.forEach((group, groupIndex) => {
        if (groupIndex > 0) {
            doc.addPage();
        }

        let currentY = margins.top;

        // Add logos to header - smaller size
        // try {
        //     // Left logo - Golden Blossom
        //     doc.addImage('/GBS-Logo-Green-Yellow.png', 'PNG', margins.left + 10, currentY, 20, 11);

        //     // Right logo - Bomba Grup (adjusted for better proportions)
        //     // Original was 20x11, now using 18x12 for better aspect ratio
        //     doc.addImage('/bombagrup.png', 'PNG', pageWidth - margins.right - 27, currentY, 18, 15);
        // } catch (error) {
        //     console.warn('Could not load logos:', error);
        // }

        // Title
        currentY = 15;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('INSPEKSI PANEN (MUTU ANCAK)', pageWidth / 2, currentY, { align: 'center' });

        // Calculate total table width and center position for header alignment
        const tableWidth = 8 + 32 + 12 + 11 + 12 + 12 + 12 + 10 + 10 + 10 + 10 + 10 + 10 + 10 + 10 + 12; // Total: 179mm
        const tableMargin = (pageWidth - tableWidth) / 2; // Center position

        // Header information - 2 columns layout, aligned with table
        currentY = 25; // Lowered from 18 to avoid logo overlap
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');

        // Align with table left margin
        const col1X = tableMargin;
        const col2X = tableMargin + (tableWidth / 2);
        const valueX1 = col1X + 22;
        const valueX2 = col2X + 28;
        const lineHeight = 4;

        // Left column
        doc.text('Estate (kebun)', col1X, currentY);
        doc.text(`: ${group.estate}`, valueX1, currentY);

        doc.text('Divisi', col1X, currentY + lineHeight);
        doc.text(`: ${group.divisi}`, valueX1, currentY + lineHeight);

        doc.text(group.job_name, col1X, currentY + (lineHeight * 2));
        doc.text(`: ${cleanSupervisorName(group.supervisor)}`, valueX1, currentY + (lineHeight * 2));

        // Right column
        doc.text('Tanggal Panen', col2X, currentY);
        doc.text(`:`, valueX2, currentY);

        doc.text('Tanggal Inspeksi', col2X, currentY + lineHeight);
        doc.text(`: ${format(new Date(inspectionDate), 'dd/MM/yyyy', { locale: id })}`, valueX2, currentY + lineHeight);

        doc.text('Rotasi / Interval', col2X, currentY + (lineHeight * 2));
        doc.text(`:`, valueX2, currentY + (lineHeight * 2));

        currentY += (lineHeight * 3) + 2;

        // Build body rows with dynamic rowSpan for Pemanen/NIP per pemanen group
        const bodyData: any[] = [];
        const rowFlags: { isSubtotal: boolean; isTotal: boolean }[] = [];
        let rowNumber = 1;

        const pemanenGroups = groupByPemanen(group.items);
        const pemanenArray = Array.from(pemanenGroups.entries());

        pemanenArray.forEach(([pemanenName, pemanenItems]) => {
            const span = pemanenItems.length; // merge down across number of rows in this group
            pemanenItems.forEach((item, idx) => {
                if (idx === 0) {
                    bodyData.push([
                        { content: rowNumber.toString(), rowSpan: span },
                        { content: pemanenName, rowSpan: span },
                        item.block || '',
                        item.no_baris || '',
                        fmt(item.pkk_diperiksa || ''),
                        fmt(item.pkk_panen || ''),
                        fmt(item.jjg_dipanen || ''),
                        fmt(item.P00 || ''),
                        fmt(item.P01 || ''),
                        formatDecimal(item.P02 || 0),
                        fmt(item.P03 || ''),
                        fmt(item.P04 || ''),
                        fmt(item.P05 || ''),
                        fmt(item.P06 || ''),
                        fmt(item.P07 || ''),
                        formatNumber(item.total_rupiah || 0),
                    ]);
                } else {
                    bodyData.push([
                        // omit No and Pemanen columns (both covered by rowSpan)
                        item.block || '',
                        item.no_baris || '',
                        fmt(item.pkk_diperiksa || ''),
                        fmt(item.pkk_panen || ''),
                        fmt(item.jjg_dipanen || ''),
                        fmt(item.P00 || ''),
                        fmt(item.P01 || ''),
                        formatDecimal(item.P02 || 0),
                        fmt(item.P03 || ''),
                        fmt(item.P04 || ''),
                        fmt(item.P05 || ''),
                        fmt(item.P06 || ''),
                        fmt(item.P07 || ''),
                        formatNumber(item.total_rupiah || 0),
                    ]);
                }
                rowFlags.push({ isSubtotal: false, isTotal: false });
            });

            // Subtotal row
            const subtotal = calculateSubtotals(pemanenItems);
            bodyData.push([
                '',
                'Sub Total',
                '',
                '',
                fmt(subtotal.pkk_diperiksa || ''),
                fmt(subtotal.pkk_panen || ''),
                fmt(subtotal.jjg_dipanen || ''),
                fmt(subtotal.P00 || ''),
                fmt(subtotal.P01 || ''),
                formatDecimal(subtotal.P02 || 0),
                fmt(subtotal.P03 || ''),
                fmt(subtotal.P04 || ''),
                fmt(subtotal.P05 || ''),
                fmt(subtotal.P06 || ''),
                fmt(subtotal.P07 || ''),
                formatNumber(subtotal.total_rupiah || 0),
            ]);
            rowFlags.push({ isSubtotal: true, isTotal: false });
            rowNumber++;
        });

        // Grand total row
        const grandTotal = calculateSubtotals(group.items);
        bodyData.push([
            '',
            'Total',
            '',
            '',
            fmt(grandTotal.pkk_diperiksa || ''),
            fmt(grandTotal.pkk_panen || ''),
            fmt(grandTotal.jjg_dipanen || ''),
            fmt(grandTotal.P00 || ''),
            fmt(grandTotal.P01 || ''),
            formatDecimal(grandTotal.P02 || 0),
            fmt(grandTotal.P03 || ''),
            fmt(grandTotal.P04 || ''),
            fmt(grandTotal.P05 || ''),
            fmt(grandTotal.P06 || ''),
            fmt(grandTotal.P07 || ''),
            formatNumber(grandTotal.total_rupiah || 0),
        ]);
        rowFlags.push({ isSubtotal: false, isTotal: true });



        // Generate main table with improved styling (tableWidth and tableMargin already defined above)
        autoTable(doc, {
            startY: currentY,
            margin: { left: tableMargin, right: pageWidth - tableMargin - tableWidth },
            head: [
                [
                    { content: 'No', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Pemanen / NIP', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Blok', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'No. Baris', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Pkk\nDiperiksa', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Pkk\nPanen', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'Jjg\nDipanen', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                    { content: 'P00', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P01', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P02', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P03', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P04', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P05', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P06', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'P07', colSpan: 1, styles: { halign: 'center', fontStyle: 'bold' } },
                    { content: 'Rupiah', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } },
                ],
                [
                    { content: 'Jjg', styles: { halign: 'center', fontStyle: 'bold' } }, // P00 - tidak ada losses
                    { content: 'Jjg', styles: { halign: 'center', fontStyle: 'bold' } }, // P01 - TBS matang tidak dipanen
                    { content: 'Kg', styles: { halign: 'center', fontStyle: 'bold' } },  // P02 - Brondolan tidak dikutip
                    { content: 'Pkk', styles: { halign: 'center', fontStyle: 'bold' } }, // P03 - Pelepah sengkleh
                    { content: 'Pkk', styles: { halign: 'center', fontStyle: 'bold' } }, // P04 - Pelepah tidak disusun rapi
                    { content: 'Jjg', styles: { halign: 'center', fontStyle: 'bold' } }, // P05 - Buah tidak diangkut
                    { content: 'Jjg', styles: { halign: 'center', fontStyle: 'bold' } }, // P06 - Buah busuk
                    { content: 'Jjg', styles: { halign: 'center', fontStyle: 'bold' } }, // P07 - Buah bintang
                ],
            ],
            body: bodyData,
            theme: 'grid',
            tableWidth: 'wrap',
            styles: {
                fontSize: 5,
                cellPadding: 0.8,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                halign: 'center',
                valign: 'middle',
                minCellHeight: 3.5,
                overflow: 'hidden',
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                halign: 'center',
                valign: 'middle',
                fontSize: 5,
                minCellHeight: 3.5,
                lineWidth: 0.1,
            },
            columnStyles: {
                0: { cellWidth: 8, halign: 'center' }, // No
                1: { cellWidth: 32, halign: 'left' }, // Pemanen/NIP
                2: { cellWidth: 12, halign: 'center' }, // Blok
                3: { cellWidth: 11, halign: 'right' }, // No. Baris
                4: { cellWidth: 12, halign: 'right' }, // Pkk Diperiksa
                5: { cellWidth: 12, halign: 'right' }, // Pkk Panen
                6: { cellWidth: 12, halign: 'right' }, // Jjg Dipanen
                7: { cellWidth: 10, halign: 'right' }, // P00
                8: { cellWidth: 10, halign: 'right' }, // P01
                9: { cellWidth: 10, halign: 'right' }, // P02
                10: { cellWidth: 10, halign: 'right' }, // P03
                11: { cellWidth: 10, halign: 'right' }, // P04
                12: { cellWidth: 10, halign: 'right' }, // P05
                13: { cellWidth: 10, halign: 'right' }, // P06
                14: { cellWidth: 10, halign: 'right' }, // P07
                15: { cellWidth: 12, halign: 'right' }, // P07
            },
            didParseCell: (data) => {
                if (data.section === 'body') {
                    const flags = rowFlags[data.row.index];
                    if (flags?.isSubtotal || flags?.isTotal) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.textColor = [0, 0, 0];
                        if (data.column.index <= 3) {
                            data.cell.styles.halign = 'center';
                        }
                    }
                }
            },
        });

        // Get Y position after main table
        const finalY = (doc as any).lastAutoTable.finalY + 1.5;

        // Build penalty data from API (penaltyRates) instead of hardcoded
        const keteranganData: any[] = [];

        if (penaltyRates && penaltyRates.length > 0) {
            // Group penalty rates into pairs (2 columns layout)
            for (let i = 0; i < penaltyRates.length; i += 2) {
                const left = penaltyRates[i];
                const right = penaltyRates[i + 1];

                const row = [
                    left.code,
                    left.name,
                    formatNumber(left.rate),
                ];

                if (right) {
                    row.push(right.code);
                    row.push(right.name);
                    row.push(formatNumber(right.rate));
                } else {
                    row.push('');
                    row.push('');
                    row.push('');
                }

                keteranganData.push(row);
            }
        }

        // Use the same margin as main table to ensure alignment
        // Calculate column widths to match main table width (179mm total)
        // Layout: Code (10mm) + Deskripsi (79.5mm) + Rupiah (12mm) per column
        const keteranganColWidth = (tableWidth / 2); // Split table width evenly with small gap
        const codeWidth = 10;
        const rupiahWidth = 12;
        const deskripsiWidth = keteranganColWidth - codeWidth - rupiahWidth;

        autoTable(doc, {
            startY: finalY,
            margin: { left: tableMargin, right: pageWidth - tableMargin - tableWidth },
            tableWidth: tableWidth,
            head: [[
                {
                    content: 'Keterangan',
                    colSpan: 6,
                    styles: {
                        halign: 'center',
                        fontStyle: 'bold' as const,
                        fontSize: 6,
                    }
                }
            ], [
                { content: 'Code', styles: { halign: 'center', fontStyle: 'bold' as const, fontSize: 5 } },
                { content: 'Deskripsi', styles: { halign: 'center', fontStyle: 'bold' as const, fontSize: 5 } },
                { content: 'Rupiah', styles: { halign: 'center', fontStyle: 'bold' as const, fontSize: 5 } },
                { content: 'Code', styles: { halign: 'center', fontStyle: 'bold' as const, fontSize: 5 } },
                { content: 'Deskripsi', styles: { halign: 'center', fontStyle: 'bold' as const, fontSize: 5 } },
                { content: 'Rupiah', styles: { halign: 'center', fontStyle: 'bold' as const, fontSize: 5 } },
            ]],
            body: keteranganData,
            theme: 'grid',
            styles: {
                fontSize: 5,
                cellPadding: 1,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                minCellHeight: 3.5,
                valign: 'top',
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold' as const,
                halign: 'center' as const,
                fontSize: 5,
                lineWidth: 0.1,
                minCellHeight: 3.5,
            },
            columnStyles: {
                0: { cellWidth: codeWidth, halign: 'center' as const },
                1: { cellWidth: deskripsiWidth, halign: 'left' as const },
                2: { cellWidth: rupiahWidth, halign: 'right' as const },
                3: { cellWidth: codeWidth, halign: 'center' as const },
                4: { cellWidth: deskripsiWidth, halign: 'left' as const },
                5: { cellWidth: rupiahWidth, halign: 'right' as const },
            },
        });

        const keteranganFinalY = (doc as any).lastAutoTable.finalY + 1.5;

        // Add Kesimpulan/Saran table with same alignment as other tables
        autoTable(doc, {
            startY: keteranganFinalY,
            margin: { left: tableMargin, right: pageWidth - tableMargin - tableWidth },
            tableWidth: 'wrap',
            head: [[
                {
                    content: 'Kesimpulan/Saran',
                    styles: {
                        halign: 'center' as const,
                        fontStyle: 'bold' as const,
                        fontSize: 5,
                    }
                }
            ]],
            body: [['']],
            theme: 'grid',
            styles: {
                fontSize: 5,
                cellPadding: 0.8,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                minCellHeight: 10,
                valign: 'top' as const,
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold' as const,
                halign: 'center' as const,
                fontSize: 5,
                lineWidth: 0.1,
                minCellHeight: 2.8,
            },
            columnStyles: {
                0: { cellWidth: tableWidth },
            },
        });

        const kesimpulanFinalY = (doc as any).lastAutoTable.finalY + 3;

        // Add signature section with professional layout
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');

        // Signature positions optimized for A4 paper with better margins
        const signatureY = kesimpulanFinalY;
        const signatureSpacing = 16;
        const sig1X = margins.left + 35;
        const sig2X = pageWidth / 2;
        const sig3X = pageWidth - margins.right - 35;

        const lineHeight2 = 4;
        const signatureLineY = signatureY + (lineHeight2 * 3.5);
        const dateY = signatureLineY + lineHeight2;

        // Mandor signature section (left)
        doc.setFont('helvetica', 'bold');
        // --- Add signature section (Boxed Style) ---
        doc.setFontSize(6);

        const sigY = kesimpulanFinalY;
        const boxWidth = 45;          // Lebar kotak per orang
        const headerHeight = 5;       // Tinggi bagian atas (Yang Mengawasi)
        const middleHeight = 20;      // Tinggi bagian tengah (Nama/TTD)
        const footerHeight = 5;       // Tinggi bagian bawah (Tanggal)
        const totalBoxHeight = headerHeight + middleHeight + footerHeight;

        const jobLvl = group.job_level ? group.job_level.charAt(0).toUpperCase() : '';
        let signatures: any[] = [];

        if (jobLvl === 'C') {
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' }
            ];
        } else if (jobLvl === 'D') {
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' },
                { title: 'Diketahui oleh,', name: group.manager || '(______________)', role: 'manager' }
            ];
        } else if (jobLvl === 'E') {
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' },
                { title: 'Diketahui oleh,', name: group.askep || '(______________)', role: 'askep' },
                { title: 'Diketahui oleh,', name: group.manager || '(______________)', role: 'manager' }
            ];
        } else {
            // Else: displays 4 boxes (Supervisor, Asisten, Askep, Manager)
            signatures = [
                { title: 'Yang memeriksa,', name: cleanSupervisorName(group.supervisor), role: 'supervisor' },
                { title: 'Diketahui oleh,', name: group.asisten || '(______________)', role: 'asisten' },
                { title: 'Diketahui oleh,', name: group.askep || '(______________)', role: 'askep' },
                { title: 'Diketahui oleh,', name: group.manager || '(______________)', role: 'manager' }
            ];
        }

        doc.setLineWidth(0.1);
        // Create boxes
        signatures.forEach((sig, index) => {
            const x = tableMargin + (boxWidth * index);

            // outer box
            doc.rect(x, sigY, boxWidth, totalBoxHeight);

            // horizontal lines to divide header/middle/footer
            doc.line(x, sigY + headerHeight, x + boxWidth, sigY + headerHeight);

            // horizontal line to divide middle/footer
            doc.line(x, sigY + headerHeight + middleHeight, x + boxWidth, sigY + headerHeight + middleHeight);



            // Header: Judul (Bold)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(5.5);
            doc.text(sig.title, x + (boxWidth / 2), sigY + 3.5, { align: 'center' });

            // Middle: Nama
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);

            const rawName = sig.name || '(______________)';
            // prevent overlapping text by splitting into multiple lines if too long for the box
            const splitName = doc.splitTextToSize(rawName, boxWidth - 4);


            const lineSpacing = 3;
            const bottomY = sigY + headerHeight + middleHeight - 1;
            const startYName = bottomY - ((splitName.length - 1) * lineSpacing);

            // print name
            doc.text(splitName, x + (boxWidth / 2), startYName, { align: 'center' });

            // Footer: Date
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.text('Tanggal :', x + 2, sigY + headerHeight + middleHeight + 3.5);
        });
    });

    return doc;
};

/**
 * Download PDF file
 */
export const downloadMutuAncakPDF = (
    data: MutuAncakData[],
    inspectionDate: string,
    penaltyRates?: PenaltyRate[]
) => {
    const groupedData = groupDataBySupervisor(data);
    const doc = generateMutuAncakPDF(groupedData, inspectionDate, penaltyRates);

    const filename = `Mutu_Ancak_${format(new Date(inspectionDate), 'yyyy-MM-dd')}.pdf`;
    doc.save(filename);
    trackDownload('Form Mutu Ancak', { filename });
};
